/**
 * Request-body construction for the listing create/update endpoints.
 *
 * Moved verbatim out of handleSubmitCreate (L1 extraction). The semantics
 * are deliberately UNCHANGED, including the parts the discovery flagged as
 * questionable, because the L0 characterization net pins them:
 *
 *   - `room_id` is only set for a room listing, and explicitly nulled on an
 *     edit — while a create omits the key entirely.
 *   - `latitude`/`longitude` are appended only when parseable, so a cleared
 *     coordinate leaves the stored value untouched.
 *   - `all_inclusive` is coerced with `!!`, so this form can never send null.
 *   - `images` is never part of the body; images have their own endpoints.
 *   - `availability_status` is never part of the body; it is PATCH-only.
 *   - `slug` is never sent; it is server-owned.
 *
 * Any change here is a behaviour change, not a refactor.
 */

/**
 * Pairs the two comma-separated amenity inputs by index, filling a missing
 * side from the other one — exactly as the inline code did.
 */
export function buildAmenities(amenitiesDe, amenitiesEn) {
  const deList = amenitiesDe.split(",").map((s) => s.trim()).filter(Boolean);
  const enList = amenitiesEn.split(",").map((s) => s.trim()).filter(Boolean);
  const maxLen = Math.max(deList.length, enList.length);
  return Array.from({ length: maxLen }, (_, i) => ({
    label_de: deList[i] || enList[i] || "",
    label_en: enList[i] || deList[i] || "",
  })).filter((a) => a.label_de || a.label_en);
}

export function buildListingPayload({
  form,
  listingType,
  amenitiesDe,
  amenitiesEn,
  editingListingId,
}) {
  const amenities = buildAmenities(amenitiesDe, amenitiesEn);

  const body = {
    unit_id: form.unit_id.trim(),
    city_id: form.city_id.trim(),
    title_de: form.title_de.trim() || "",
    title_en: form.title_en.trim() || "",
    description_de: form.description_de.trim() || "",
    description_en: form.description_en.trim() || "",
    price_chf_month: parseInt(form.price_chf_month, 10) || 0,
    bedrooms: parseInt(form.bedrooms, 10) || 0,
    bathrooms: parseInt(form.bathrooms, 10) || 0,
    size_sqm: parseInt(form.size_sqm, 10) || 0,
    address: form.address.trim() || null,
    location_description: form.location_description.trim() || null,
    show_address: !!form.show_address,
    available_from: form.available_from || null,
    is_published: !!form.is_published,
    sort_order: parseInt(form.sort_order, 10) || 0,
    // Listing schema v2 (Migration 118): empty -> null ("not maintained").
    all_inclusive: !!form.all_inclusive,
    wg_size:
      form.wg_size.trim() && !Number.isNaN(parseInt(form.wg_size, 10))
        ? parseInt(form.wg_size, 10)
        : null,
    floor: form.floor.trim() || null,
    district_de: form.district_de.trim() || null,
    district_en: form.district_en.trim() || null,
    min_stay_months:
      form.min_stay_months.trim() && !Number.isNaN(parseInt(form.min_stay_months, 10))
        ? parseInt(form.min_stay_months, 10)
        : null,
    lease_type: form.lease_type || null,
    flatmates_description_de: form.flatmates_description_de.trim() || null,
    flatmates_description_en: form.flatmates_description_en.trim() || null,
    amenities,
  };

  if (listingType === "room" && form.room_id.trim()) {
    body.room_id = form.room_id.trim();
  } else if (editingListingId) {
    body.room_id = null;
  }
  if (form.latitude.trim() && !Number.isNaN(parseFloat(form.latitude))) body.latitude = parseFloat(form.latitude);
  if (form.longitude.trim() && !Number.isNaN(parseFloat(form.longitude))) body.longitude = parseFloat(form.longitude);

  return body;
}
